import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getButtonClass } from '../../../../components/popup/popupUtils';
import { sanitizeHtml } from '../../../../assets/utils/comm/sanitize';

/**
 * 서명 팝업 컴포넌트
 * HTML5 Canvas를 활용한 전자서명 입력 컴포넌트
 */
const SignaturePopup = ({ config, onClose, displayType = 'modal' }) => {
  const { t } = useTranslation();
  const [isDrawing, setIsDrawing] = useState(false);
  const signatureCanvasRef = useRef(null);
  const signatureContextRef = useRef(null);
  const lastPointRef = useRef({ x: 0, y: 0 });

  const loadSignatureToCanvas = useCallback((dataUrl) => {
    const canvas = signatureCanvasRef.current;
    const ctx = signatureContextRef.current;
    if (!canvas || !ctx || !dataUrl) return;

    const rect = canvas.getBoundingClientRect();
    const img = new Image();
    img.onload = () => {
      // 기존 내용 클리어(투명) 후 이미지 로드
      ctx.clearRect(0, 0, rect.width, rect.height);

      // 캔버스 영역에 "비율 유지(contain)"로 맞춰 그리기
      const iw = img.naturalWidth || img.width || 1;
      const ih = img.naturalHeight || img.height || 1;
      const scale = Math.min(rect.width / iw, rect.height / ih);
      const dw = iw * scale;
      const dh = ih * scale;
      const dx = (rect.width - dw) / 2;
      const dy = (rect.height - dh) / 2;

      ctx.drawImage(img, dx, dy, dw, dh);
    };
    img.src = dataUrl;
  }, []);

  const initSignatureCanvas = useCallback(() => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      signatureContextRef.current = ctx;
      // 캔버스 크기 설정
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      // DPR 스케일 적용(좌표는 CSS px 기준으로 사용)
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      // 초기화: 캔버스 픽셀은 투명 상태로 유지 (저장 시 투명배경 PNG 지원)
      // 화면에서 흰 배경이 필요하면 canvas 스타일(backgroundColor)로만 처리합니다.
      ctx.clearRect(0, 0, rect.width, rect.height);
      // 선 스타일 설정
      ctx.strokeStyle = '#000000';
      // 인쇄/프리뷰에서 선이 보이도록 굵기 상향
      ctx.lineWidth = 10;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
    setIsDrawing(false);

    // 기존 서명(있으면) 불러오기
    const initialSignature = config?.initialData?.signatureImage;
    if (initialSignature) {
      // 렌더 직후 레이아웃 안정화 후 로드
      requestAnimationFrame(() => loadSignatureToCanvas(initialSignature));
    }
  }, [config?.initialData?.signatureImage, loadSignatureToCanvas]);

  // 캔버스 초기화
  useEffect(() => {
    requestAnimationFrame(() => initSignatureCanvas());
  }, [initSignatureCanvas]);

  // 마우스/터치 좌표 변환
  const getPointFromEvent = useCallback((e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();

    if (e.touches && e.touches.length > 0) {
      return {
        x: (e.touches[0].clientX - rect.left),
        y: (e.touches[0].clientY - rect.top),
      };
    } else {
      return {
        x: (e.clientX - rect.left),
        y: (e.clientY - rect.top),
      };
    }
  }, []);

  // 그리기 시작
  const startDrawing = useCallback((e) => {
    e.preventDefault();
    const point = getPointFromEvent(e);
    if (!point) return;

    setIsDrawing(true);
    lastPointRef.current = point;
    const ctx = signatureContextRef.current;
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(point.x, point.y);
    }
  }, [getPointFromEvent]);

  // 캔버스 안으로 들어올 때 그리기 재개
  const resumeDrawing = useCallback((e) => {
    // 마우스 버튼이 눌려있고, 그리기가 중단된 상태일 때만 재개
    if (e.buttons === 1 && !isDrawing) {
      const point = getPointFromEvent(e);
      if (point) {
        setIsDrawing(true);
        lastPointRef.current = point;
        const ctx = signatureContextRef.current;
        if (ctx) {
          ctx.beginPath();
          ctx.moveTo(point.x, point.y);
        }
      }
    }
  }, [isDrawing, getPointFromEvent]);

  // 그리기 진행
  const draw = useCallback((e) => {
    e.preventDefault();

    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    // 캔버스 영역 안에 있는지 확인
    const rect = canvas.getBoundingClientRect();
    let isInsideCanvas = false;

    if (e.touches && e.touches.length > 0) {
      const touch = e.touches[0];
      isInsideCanvas =
        touch.clientX >= rect.left &&
        touch.clientX <= rect.right &&
        touch.clientY >= rect.top &&
        touch.clientY <= rect.bottom;
    } else {
      isInsideCanvas =
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;
    }

    // 캔버스 밖에 있으면 그리기 중단
    if (!isInsideCanvas) {
      if (isDrawing) {
        setIsDrawing(false);
        const ctx = signatureContextRef.current;
        if (ctx) {
          ctx.closePath();
        }
      }
      return;
    }

    // 캔버스 안에 있고, 그리기 중이 아니지만 마우스 버튼이 눌려있으면 그리기 재개
    if (!isDrawing && (e.buttons === 1 || (e.touches && e.touches.length > 0))) {
      const point = getPointFromEvent(e);
      if (point) {
        setIsDrawing(true);
        lastPointRef.current = point;
        const ctx = signatureContextRef.current;
        if (ctx) {
          ctx.beginPath();
          ctx.moveTo(point.x, point.y);
        }
      }
      return;
    }

    if (!isDrawing) return;

    const point = getPointFromEvent(e);
    if (!point) return;

    const ctx = signatureContextRef.current;
    if (ctx) {
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
    }

    lastPointRef.current = point;
  }, [isDrawing, getPointFromEvent]);

  // 그리기 종료
  const stopDrawing = useCallback((e) => {
    e.preventDefault();
    if (isDrawing) {
      setIsDrawing(false);
      const ctx = signatureContextRef.current;
      if (ctx) {
        ctx.closePath();
      }
    }
  }, [isDrawing]);

  // 서명 지우기
  const clearSignature = useCallback(() => {
    const canvas = signatureCanvasRef.current;
    const ctx = signatureContextRef.current;
    if (canvas && ctx) {
      // 지우기: 투명 상태로 초기화 (저장 시 투명 유지)
      // DPR transform이 걸려있어서, 전체 픽셀 기준으로 확실하게 지움
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const dpr = window.devicePixelRatio || 1;
      ctx.scale(dpr, dpr);
    }
  }, []);

  // 서명 이미지로 변환
  const getSignatureImage = useCallback(() => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      return canvas.toDataURL('image/png');
    }
    return null;
  }, []);

  // 왼쪽 버튼 클릭 (지우기)
  const handleLeftButtonClick = () => {
    const leftBtn = config?.leftButton;
    if (leftBtn?.submit) {
      const imageData = getSignatureImage();
      onClose(true, { signatureImage: imageData });
    } else {
      // 지우기: 캔버스만 초기화하고 창은 닫지 않음
      clearSignature();
    }
  };

  // 오른쪽 버튼 클릭 (서명완료)
  const handleRightButtonClick = () => {
    const rightBtn = config?.rightButton;
    if (rightBtn?.submit) {
      const imageData = getSignatureImage();
      onClose(true, { signatureImage: imageData });
    } else {
      onClose(false);
    }
  };


  // 팝업 콘텐츠 클래스명 결정
  const getContentClassName = () => {
    return displayType === 'slide'
      ? 'popupContent popup-signature popupContent-slide'
      : 'popupContent popup-signature';
  };

  return (
    <div className={getContentClassName()}>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a
        className="popup-close"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          onClose(false);
        }}
      >
        <i className="pe-7s-close"></i>
      </a>
      <div className="container">
        <div className="main-popup-title">
          <div className="padding-10">
            <h3 style={{ marginTop: '10px' }}>{config?.title || t('popup.signature.title')}</h3>
          </div>
        </div>
        <div className="main-popup-body popup-signature">
          <div
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(config?.content || t('popup.signature.defaultContent')),
            }}
          />
          <div className="signature-canvas-wrapper" style={{ marginTop: '20px' }}>
            <canvas
              ref={signatureCanvasRef}
              style={{
                width: '100%',
                height: '200px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'crosshair',
                backgroundColor: '#ffffff',
                touchAction: 'none',
              }}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseEnter={resumeDrawing}
              onMouseLeave={(e) => {
                // 마우스가 캔버스를 벗어날 때만 그리기 중단
                if (isDrawing) {
                  stopDrawing(e);
                }
              }}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>
        </div>
        <div className="main-popup-footer d-flex justify-content-between" style={{ gap: '10px' }}>
          {config?.leftButton && (
            <button
              type="button"
              className={`btn main-popup-left-btn ${getButtonClass(config.leftButton.color || 'btn-secondary')}`}
              onClick={handleLeftButtonClick}
            >
              {config.leftButton.text || t('popup.signature.clear')}
            </button>
          )}
              {config?.rightButton && (
                <button
                  type="button"
                  className={`btn main-popup-right-btn ${getButtonClass(config.rightButton.color || 'btn-primary')}`}
                  onClick={handleRightButtonClick}
                >
                  {config.rightButton.text || t('popup.signature.complete')}
                </button>
              )}
        </div>
      </div>
    </div>
  );
};

export default SignaturePopup;

